// Report view — restyled onto the lens system's semantic tokens
// (mainDivider / mainButtonBg / mainText / mutedText / velBlue / inputFocus).
// The local THEMES object + MutationObserver dark-detection are gone: the
// tokens track light/dark themselves, same as LensToolbar / TimelineStrip.
import { useState, useEffect, useMemo } from "react";
import { FileText, RefreshCw, FileDown, Loader2 } from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import { useCompanyContext } from "../../../context";
import { getReportsII } from "../../../api/wiki";
import TileReport from "./TileReportII";
import ReportPDF, { CONFIG, buildFileName } from "../Wiki/tiles/ReportPDF";

/* ── Section grouping ── */
// API returns flat section objects keyed by target_type
// (one or more versions per section). Bucket by target_type,
// newest first within each bucket, sections in numeric order.
const sectionNum = (key) => {
  const m = String(key || "").match(/\d+/);
  return m ? parseInt(m[0], 10) : Number.MAX_SAFE_INTEGER;
};

const groupSectionsAcrossReports = (items) => {
  const bucket = new Map();
  (items || []).forEach((item) => {
    const key = item?.target_type;
    if (!key) return;
    if (!bucket.has(key)) bucket.set(key, []);
    bucket.get(key).push(item);
  });
  for (const versions of bucket.values()) {
    versions.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  }
  return [...bucket.entries()]
    .sort(([a], [b]) => sectionNum(a) - sectionNum(b))
    .map(([sectionKey, versions]) => ({ sectionKey, versions }));
};

/* ── Skeleton ── */
const TileSkeleton = ({ delay = 0 }) => (
  <div
    className="overflow-hidden rounded-xl border border-mainDivider bg-reortTileBg"
    style={{ minHeight: 160, animationDelay: `${delay}ms` }}
  >
    <div className="flex items-center justify-between px-4 pb-2.5 pt-3.5">
      <div className="h-3.5 w-2/5 animate-pulse rounded-md bg-mainButtonBg/50" />
      <div className="flex gap-2">
        <div className="h-6 w-20 animate-pulse rounded-lg bg-mainButtonBg/50" />
        <div className="h-6 w-14 animate-pulse rounded-lg bg-mainButtonBg/50" />
      </div>
    </div>
    <div className="mx-4 border-t border-mainDivider" />
    <div className="flex flex-col gap-2.5 px-4 pb-4 pt-3">
      <div className="h-3 w-full animate-pulse rounded bg-mainButtonBg/50" />
      <div className="h-3 w-5/6 animate-pulse rounded bg-mainButtonBg/50" />
      <div className="h-3 w-4/6 animate-pulse rounded bg-mainButtonBg/50" />
    </div>
  </div>
);

/* ── DefaultReportView ── */
const DefaultReportView = () => {
  const { currentCompany } = useCompanyContext();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedBySection, setSelectedBySection] = useState({});

  const companyName = currentCompany?.case || "";
  const sections = useMemo(
    () => groupSectionsAcrossReports(reports),
    [reports],
  );

  const loadReports = () => {
    setLoading(true);
    setError(false);
    setVisible(false);
    setSelectedBySection({});
    getReportsII()
      .then((res) => {
        setReports(res?.data || []);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setVisible(true)),
        );
      })
      .catch((err) => {
        console.error("[DefaultReportView] getReportsII rejected", err);
        setReports([]);
        setError(true);
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadReports();
  }, [companyName]);

  const handleSectionVersionChange = (sectionKey, idx) => {
    setSelectedBySection((prev) => ({ ...prev, [sectionKey]: idx }));
  };

  const handleExportAll = async () => {
    if (isExporting || sections.length === 0) return;
    setIsExporting(true);
    try {
      const reportsForPdf = sections
        .map(({ sectionKey, versions }) => {
          const idx = selectedBySection[sectionKey] ?? 0;
          const selected = versions[idx] || versions[0];
          if (!selected) return null;
          return {
            title: selected.section_title || sectionKey,
            target_type: sectionKey,
            versions: [selected],
          };
        })
        .filter(Boolean);

      const blob = await pdf(
        <ReportPDF reports={reportsForPdf} companyName={companyName} />,
      ).toBlob();

      const today = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = buildFileName({
        entityTitle: CONFIG.entityTitle,
        companyName,
        timestamp: today,
      });
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error("[DefaultReportView] PDF export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full overflow-y-auto font-poppins font-normal text-mainText">
        <div className="mx-auto px-8 pb-8 pt-16" style={{ maxWidth: 1000 }}>
          <div className="mb-6 flex items-center justify-end">
            <div className="h-7 w-28 animate-pulse rounded-lg bg-mainButtonBg/50" />
          </div>
          <div className="flex flex-col gap-4">
            {Array.from({ length: 4 }, (_, i) => (
              <TileSkeleton key={i} delay={i * 60} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 pt-14 font-poppins font-normal">
        <FileText className="h-8 w-8 text-mutedText/50" strokeWidth={1.5} />
        <p className="text-[13px] text-mutedText">Failed to load reports</p>
        <button
          onClick={loadReports}
          className="flex items-center gap-1.5 rounded-sm text-[11px] uppercase tracking-[0.14em] text-velBlue transition-opacity duration-150 hover:opacity-75 focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
        >
          <RefreshCw size={11} strokeWidth={2} />
          Try again
        </button>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 pt-14 font-poppins font-normal">
        <FileText className="h-8 w-8 text-mutedText/50" strokeWidth={1.5} />
        <p className="text-[13px] text-mutedText">No reports yet.</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto font-poppins font-normal text-mainText">
      <div className="mx-auto px-8 pb-8 pt-16" style={{ maxWidth: 1000 }}>
        <div className="mb-6 flex items-center justify-end">
          {/* Tinted with the tiles' velBlue accent (window pill / active
              Changes button), glowing on hover like the tiles do */}
          <button
            type="button"
            onClick={handleExportAll}
            disabled={isExporting}
            className="inline-flex items-center gap-1.5 rounded-lg border border-velBlue/30 bg-velBlue/10 px-3 py-1.5 text-[11px] font-medium text-velBlue backdrop-blur-sm transition-all duration-200 hover:border-velBlue/45 hover:bg-velBlue/15 hover:shadow-[0_0_14px_rgba(14,165,233,0.18)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:shadow-none"
          >
            {isExporting ? (
              <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
            ) : (
              <FileDown className="h-3 w-3" strokeWidth={2} />
            )}
            {isExporting ? "Exporting…" : "Export PDF"}
          </button>
        </div>

        <div className="flex flex-col gap-4">
          {sections.map(({ sectionKey, versions }, index) => (
            <div
              key={sectionKey}
              style={{
                transition: `opacity 0.4s ease-out ${Math.min(index * 55, 650)}ms, transform 0.4s ease-out ${Math.min(index * 55, 650)}ms`,
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(10px)",
              }}
            >
              <TileReport
                sectionKey={sectionKey}
                versions={versions}
                selectedIdx={selectedBySection[sectionKey] ?? 0}
                onSelectVersion={(idx) =>
                  handleSectionVersionChange(sectionKey, idx)
                }
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DefaultReportView;
